import {
    ChangeDetectionStrategy,
    Component,
    NgZone,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";

import { BusyService } from "~app/common/components/busy/busy.service";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { MattslagskonverteringNumericStyledHotCell } from "./mattslagskonvertering.hot-cell";
import {
    IMattslagResponse,
    IMattslagskonvertering,
    IPeriod,
    IResultatomrade,
    MattslagRequest,
    MattslagSaveRequest
} from "./mattslagskonvertering.models";
import { MattslagskonverteringService } from "./mattslagskonvertering.service";

@Component({
    selector: "rask-mattslagskonvertering",
    templateUrl: "./mattslagskonvertering.component.html",
    styleUrls: ["./mattslagskonvertering.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        SelectorComponent,
        FormsModule,
        TextmultilineComponent,
        ButtonComponent,
        HotDocComponent,
        SaveButtonComponent
    ]
})
export class MattslagskonverteringComponent implements OnInit {
    activeFilter: MattslagRequest;
    editableRole: boolean;
    filterText: string;
    hotDoc: HotDoc;
    internalRequest: MattslagRequest = new MattslagRequest();
    isDirty: boolean;
    marknadsomraden: SimpleSelectionItem[];
    mattslag: SimpleSelectionItem[];
    mattslagResponse: IMattslagResponse;
    months = [
        "Januari",
        "Februari",
        "Mars",
        "April",
        "Maj",
        "Juni",
        "Juli",
        "Augusti",
        "September",
        "Oktober",
        "November",
        "December"
    ];
    resultatomraden: IResultatomrade[];
    standardVardeName = "Standardvärde";

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly mattslagskonverteringservice = inject(MattslagskonverteringService);
    private readonly modalService = inject(RaskModalService);
    private readonly ngZone = inject(NgZone);

    canFilter = (): boolean => (
        this.internalRequest.marknadsomradeId !== undefined && this.internalRequest.mattslagId !== undefined
    );

    canSave(): boolean {
        return this.isDirty;
    }

    cancelChanges(): void {
        const settings = new ModalSettings("Vill du ångra alla ändringar?", `Dina ändringar kommer inte att sparas,
             Välj Nej, avbryt för att stanna kvar på sidan så att du kan spara dina ändringar.`);
        settings.yesCaption = "Ja, ångra ändringar";
        settings.noCaption = "Nej, avbryt";
        this.modalService.confirm(settings)
            .subscribe(undo => {
                if (undo === ConfirmDialogResponse.save) {
                    this.loadNewMattslag(this.internalRequest);
                }
            });
    }

    createCellHSG(propertyName: string, index: number, row: HotRow, mattslagHSG: IMattslagskonvertering): MattslagskonverteringNumericStyledHotCell {
        let period: IPeriod = null;
        let onchange: (cellchangeArg: any) => void;
        let value: number;

        if (propertyName === "standardVarde") {
            value = mattslagHSG.perioder[index].konverteringsFaktor;
            period = mattslagHSG.perioder[index];
            onchange = (cellchangeArg) => this.onStandardvardeRowEditing(mattslagHSG, period, cellchangeArg);
        } else {
            period = mattslagHSG.perioder[index + 1];
            onchange = (cellchangeArg) => this.onCellEditing(mattslagHSG, period, cellchangeArg);
            value = mattslagHSG.perioder[index + 1].konverteringsFaktor;
        }

        const flags: Cellstyle = this.getCustomFlags(propertyName, period);
        const classList = this.getCustomCellStyles(propertyName, period);

        return row.add(propertyName, new MattslagskonverteringNumericStyledHotCell(value, classList, flags, onchange));
    }

    createRowHSG(doc: HotDoc, mattslagHSG: IMattslagskonvertering) {
        doc.createRow(row => {
            row.addText("handelssortimentsGrupp", mattslagHSG.produktgrupp, Cellstyle.Readonly);
            this.createCellHSG("standardVarde", 0, row, mattslagHSG);

            this.months.forEach((month, index) => {
                this.createCellHSG(month, index, row, mattslagHSG);
            });
        });
    }

    deleteStandardVardeRowStandardVarde(period: IPeriod, isMasterValue: boolean): void {
        if (!isMasterValue) {
            this.onRowDelete(period);
        }
    }

    dirtyCheckCallback(result: IDirtyFormResult): void {
        // Krav från dirtyhandlern
    }

    getColumns = (): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];
        columns.push({
            header: "Handelssortimentsgrupp",
            cellName: "handelssortimentsGrupp"
        });
        columns.push({
            header: this.standardVardeName,
            cellName: "standardVarde",
            type: "numeric",
            width: 100
        });
        this.months.forEach((month) => {
            columns.push({
                header: month,
                cellName: month,
                type: "numeric",
                width: 80
            });
        });
        return columns;
    };

    getCustomCellStyles(propertyName: string, period: IPeriod): string[] {
        const classList: string[] = [];
        if (this.isReadonly(propertyName, period)) {
            classList.push("mattslag-readonly");
        }

        if (period.standardVardeOnSave) {
            classList.push("mattslag-standardonsave");
        }

        if (propertyName === "standardVarde") {
            if (period.inherited) {
                classList.push("mattslag-inherited");
            }
        } else {
            if (period && period.konverteringsFaktor > 0) {
                if (period.inherited) {
                    classList.push("mattslag-inherited");
                } else {
                    classList.push("mattslag-overridden");
                }
            } else {
                classList.push("mattslag-normal");
            }
        }
        return classList;
    }

    getCustomFlags(propertyName: string, period: IPeriod): Cellstyle {
        let flags: Cellstyle = null;
        if (this.isReadonly(propertyName, period)) {
            // eslint-disable-next-line no-bitwise
            flags = flags | Cellstyle.Readonly;
        }
        return flags;
    }

    getMattslagData(): void {
        this.busyService.addWork(this.mattslagskonverteringservice.getMattslagBasdata())
            .subscribe(result => {
                this.marknadsomraden = result.marknadsomraden.map(mo => ({
                    id: mo.id,
                    text: mo.namn
                } as SimpleSelectionItem));
                this.resultatomraden = result.resultatomraden;
                this.mattslag = result.mattslagList.map(ms => ({
                    id: ms.id,
                    text: ms.kortnamn
                } as SimpleSelectionItem));
                this.internalRequest.mattslagId = this.mattslag.find(x => x.text === "MWh").id;
            });
    }

    getNewMattslag(): void {
        if (this.isDirty) {
            this.modalService
                .confirm(new ModalSettings("Osparade förändringar", `Ett eller flera värden är osparade i tabellen.
                    Välj JA för att visa nya utan att spara, välj Nej för att stanna kvar på sidan så att du kan spara dina ändringar.`))
                .subscribe({
                    next: result => {
                        if (result === ConfirmDialogResponse.save) {
                            this.loadNewMattslag(this.internalRequest);
                        } else {
                            this.resetToActiveFilter();
                        }
                    }
                });
        } else {
            this.loadNewMattslag(this.internalRequest);
        }
    }

    getResultatomraden(): SimpleSelectionItem[] {
        if (!this.internalRequest.marknadsomradeId || this.internalRequest.marknadsomradeId <= 0) {
            return [];
        }
        return this.resultatomraden
            .filter(ro => ro.marknadsomradeId === this.internalRequest.marknadsomradeId)
            .map(ro => ({
                id: ro.id,
                text: ro.namn
            } as SimpleSelectionItem));
    }

    getRows(doc: HotDoc) {
        this.mattslagResponse.mattslagskonverteringar.forEach((mattslagHSG) => {
            this.createRowHSG(doc, mattslagHSG);
        });
    }

    idFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.id : undefined;

    initMatrisSettings() {
        const colFactory = () => this.getColumns();
        const rowFactory = (doc: HotDoc) => this.getRows(doc);
        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                calculateSum: false,
                numericParser: (value) => this.parseFloatWithFormat(value)
            },
            stretchH: "all",
            fixedColumnsStart: 1
        };
        this.hotDoc = new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    }

    isReadonly(propertyName: string, period: IPeriod) {
        return (period.standardVardeOnSave || this.editableRole === false);
    }

    loadNewMattslag(filter: MattslagRequest): void {
        this.busyService.addWork(this.mattslagskonverteringservice.getMattslag(filter))
            .subscribe(result => {
                this.mattslagResponse = result;
                this.initMatrisSettings();
                this.filterText = this.showSelectedFilter();
                this.activeFilter = ObjectUtils.deepCopy(filter);
                this.setDirty(false);
            });
    }

    ngOnInit(): void {
        this.editableRole = this.authenticationService.canEdit();
        this.dirtyForm.onDirtyNgModel(
            this.canSave.bind(this),
            this.save.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canSave(),
            "Osparade förändringar",
            "Ett eller flera värden är osparade i tabellen",
            {
                size: "lg",
                centered: false
            });

        this.getMattslagData();
    }

    onCellDelete(mattslagskonvertering: IMattslagskonvertering, period: IPeriod, isStandardvardeColumn: boolean): void {
        const standardVardePeriod = this.mattslagResponse.mattslagskonverteringar
            .find(x => x.produktgrupp === this.standardVardeName).perioder[period.manadNummer];
        const standardVardeHS = mattslagskonvertering.perioder[0];

        if (isStandardvardeColumn) {
            this.setCellValues(period, mattslagskonvertering.perioder[0].konverteringsFaktor, true, false, true);
            this.mattslagResponse.mattslagskonverteringar
                .forEach((m) => {
                    m.perioder.forEach((p) => {
                        const standardVardeHSRow = m.perioder[0];
                        const nykonverteringsFaktorRow = (!standardVardeHSRow.inherited) ? standardVardeHSRow.konverteringsFaktor : standardVardeHS.konverteringsFaktor;
                        if (p.manadNummer === period.manadNummer && m.produktgrupp !== this.standardVardeName && standardVardeHSRow.inherited) {
                            this.setCellValues(p, nykonverteringsFaktorRow, true, false, false);
                        }
                    });
                });
        } else {
            const nykonverteringsFaktor = (standardVardeHS.inherited !== true) ? standardVardeHS.konverteringsFaktor : standardVardePeriod.konverteringsFaktor;
            this.setCellValues(period, nykonverteringsFaktor, true, false, true);
        }
        this.setDirty(true);
    }

    // Editering i cell
    onCellEditing(mattslagskonvertering: IMattslagskonvertering, period: IPeriod, change: NumericCellChangeArg): void {
        const newkonverteringsFaktor = change.newValue;
        const isDelete: boolean = change.rawNewValue === null;
        if (mattslagskonvertering.produktgrupp === this.standardVardeName) {
            this.onCellEditingStandardVarde(mattslagskonvertering, period, newkonverteringsFaktor, isDelete);
        } else {
            if (change.rawNewValue === null) {
                this.onCellDelete(mattslagskonvertering, period, false);
            } else {
                this.setCellValues(period, newkonverteringsFaktor, false, false, true);
            }
        }

        this.setDirty(true);
        change.complete();
    }

    // Editering av Standardvärderaden i varje kolumn (ej första)
    onCellEditingStandardVarde(mattslagskonvertering: IMattslagskonvertering, period: IPeriod, newkonverteringsFaktor: number, isDelete: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (isDelete) {
            this.onCellDelete(mattslagskonvertering, period, true);
        } else {
            this.setCellValues(period, newkonverteringsFaktor, false, false, true);
            this.mattslagResponse.mattslagskonverteringar
                .forEach((m) => {
                    m.perioder.forEach((p) => {
                        // Om Standardvärde för raden är inherited--> sätt värdet
                        if (p.inherited && p.manadNummer === period.manadNummer && p.manadNummer !== null && m.perioder[0].inherited === true) {
                            this.setCellValues(p, newkonverteringsFaktor, true, false, false);
                        }
                    });
                });
        }
    }

    onRowDelete(period: IPeriod) {
        period.standardVardeOnSave = true;
    }

    // Editering i första kolumnen "Standardvärde"
    onStandardvardeRowEditing(mattslagskonvertering: IMattslagskonvertering, period: IPeriod, change: NumericCellChangeArg): void {
        const newkonverteringsFaktor = change.newValue;
        const isDelete: boolean = change.rawNewValue === null;

        if (mattslagskonvertering.produktgrupp === this.standardVardeName) {
            this.onStandardvardeRowStandardVardeEdit(isDelete, period, newkonverteringsFaktor, mattslagskonvertering);
        } else {
            this.setRowValues(mattslagskonvertering, period, newkonverteringsFaktor, isDelete);
        }

        this.setDirty(true);
        change.complete();
    }

    // Editering i första kolumnen "Standardvärde" på första raden "Standardvärde"
    onStandardvardeRowStandardVardeEdit(isDelete: boolean, period: IPeriod, newkonverteringsFaktor: number, mattslagskonvertering: IMattslagskonvertering) {
        const isMasterValue = !ObjectUtils.hasValue(this.internalRequest.resultatomradeId);

        if (isDelete) {
            this.deleteStandardVardeRowStandardVarde(period, isMasterValue);
        } else {
            this.setCellValues(period, newkonverteringsFaktor, false, false, true);
            // Sätt på alla som är inherited förutom de som har ej inherited i standardvärde!
            this.mattslagResponse.mattslagskonverteringar.forEach((m) => {
                m.perioder.forEach((p) => {
                    if ((m.produktgrupp === this.standardVardeName && p.manadNummer !== null) || p.manadNummer === null) {
                        this.setCellValues(p, newkonverteringsFaktor, true, false, false);
                    } else {
                        const standardVardeRowInherited = m.perioder[0].inherited;
                        const standardVardeColumnInherited = this.mattslagResponse.mattslagskonverteringar
                            .find(x => x.produktgrupp === this.standardVardeName).perioder.find(x => x.manadNummer === p.manadNummer).inherited;

                        if (p.inherited && standardVardeRowInherited && standardVardeColumnInherited) {
                            this.setCellValues(p, newkonverteringsFaktor, true, false, false);
                        }
                    }
                });
            });
        }
    }

    parseFloatWithFormat(value: string) {
        const num = value.replace(/,/g, ".");
        return parseFloat(num);
    }

    resetToActiveFilter = () => (this.internalRequest = this.activeFilter);

    save(): void {
        if (this.canSave()) {
            const saveRequest = new MattslagSaveRequest();
            saveRequest.marknadsomradeId = this.internalRequest.marknadsomradeId;
            saveRequest.resultatomradeId = this.internalRequest.resultatomradeId;
            saveRequest.mattslagskonverteringar = this.mattslagResponse.mattslagskonverteringar;
            saveRequest.mattslagId = this.internalRequest.mattslagId;

            this.busyService.addWork(this.mattslagskonverteringservice.saveMattslag(saveRequest))
                .subscribe(result => {
                    this.mattslagResponse = result;
                    this.initMatrisSettings();
                    this.setDirty(false);
                });
        }
    }

    setCellValues(period: IPeriod, newkonverteringsFaktor: number, inherited: boolean, standardVardeOnSave: boolean, setNotInherited: boolean): void {
        if (period.inherited || setNotInherited) {
            period.konverteringsFaktor = newkonverteringsFaktor;
            period.inherited = inherited;
            period.standardVardeOnSave = standardVardeOnSave;
            period.tooltip = "";
        }
    }

    setDirty(dirty: boolean) {
        this.ngZone.run(() => {
            this.isDirty = dirty;
            this.hotDoc.rebind();
        });
    }

    setRowValues(mattslagskonvertering: IMattslagskonvertering, period: IPeriod, newkonverteringsFaktor: number, isDelete: boolean): void {
        const standardVardeColumn = this.mattslagResponse.mattslagskonverteringar
            .find(x => x.produktgrupp === this.standardVardeName).perioder[0];

        if (isDelete) {
            if (standardVardeColumn.inherited) {
                this.onRowDelete(period);
            } else {
                this.setCellValues(period, standardVardeColumn.konverteringsFaktor, true, false, true);
            }
        } else {
            this.setCellValues(period, newkonverteringsFaktor, false, false, true);
        }

        this.mattslagResponse.mattslagskonverteringar.filter(x => x.produktgruppId === mattslagskonvertering.produktgruppId)
            .forEach((m) => {
                m.perioder.forEach((p) => {
                    if (isDelete && p.inherited) {
                        if (standardVardeColumn.inherited) {
                            this.onRowDelete(p);
                        } else {
                            this.setCellValues(p, standardVardeColumn.konverteringsFaktor, true, false, true);
                        }
                    } else if (p.manadNummer !== null) {
                        this.setCellValues(p, newkonverteringsFaktor, true, false, false);
                    }
                });
            });
    }

    showSelectedFilter(): string {
        let filtertext = "";
        let ro = "";
        if (this.internalRequest.mattslagId && this.internalRequest.marknadsomradeId) {
            const mo = this.marknadsomraden.find(x => x.id === this.internalRequest.marknadsomradeId).text;
            const ms = this.mattslag.find(x => x.id === this.internalRequest.mattslagId).text;
            if (this.internalRequest.resultatomradeId) {
                ro = `${this.resultatomraden.find(x => x.id === this.internalRequest.resultatomradeId).namn}, `;
            }
            filtertext = `Från ${ms} till m3f i ${ro} ${mo}`;
        }
        return filtertext;
    }

    textFormatter = (ssi: SimpleSelectionItem) => ssi ? ssi.text : undefined;
}
